import type { ReactNode } from 'react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface PublicOrgSubpageHeroProps {
  title: string;
  bannerUrl?: string | null;
  headingId?: string;
  backLink?: { to: string; label: string };
  children?: ReactNode;
  className?: string;
}

/** Shared hero banner for club-academy public sub-pages (lessons, court hire, join, etc.). */
export function PublicOrgSubpageHero({
  title,
  bannerUrl,
  headingId,
  backLink,
  children,
  className,
}: PublicOrgSubpageHeroProps) {
  const [failedBannerUrl, setFailedBannerUrl] = useState<string | null>(null);
  const showBanner = Boolean(bannerUrl && bannerUrl !== failedBannerUrl);

  return (
    <section
      className={cn(
        'relative isolate min-h-[11rem] w-full overflow-hidden border-b border-border/50 md:min-h-[13rem]',
        className
      )}
      aria-labelledby={headingId}
    >
      <div className="absolute inset-0">
        {showBanner ? (
          <img
            src={bannerUrl!}
            alt=""
            className="h-full w-full object-cover object-center"
            referrerPolicy="no-referrer"
            onError={() => setFailedBannerUrl(bannerUrl!)}
          />
        ) : (
          <div
            className="h-full w-full bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950"
            aria-hidden
          />
        )}
      </div>
      <div className="absolute inset-0 bg-black/55 dark:bg-black/60" aria-hidden />
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col justify-end px-4 py-8 sm:px-6 sm:py-10 lg:px-8">
        {backLink ? (
          <Link
            to={backLink.to}
            className="mb-4 inline-flex w-fit items-center gap-2 text-sm text-white/90 transition-colors hover:text-white"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            {backLink.label}
          </Link>
        ) : null}
        <h1
          id={headingId}
          className="text-2xl font-bold tracking-tight text-white drop-shadow-sm md:text-3xl"
        >
          {title}
        </h1>
        {children}
      </div>
    </section>
  );
}
